import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDown, Clock, Compass, Layers3, SquareStack } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { QuickAddMenu } from "@/features/workspace/components/QuickAddMenu";

import { BriefingSlot } from "@/features/briefing";
import { DfmRulesPage, DfmVerdictCard } from "@/features/dfm-rules";
import {
  DxfEmptyHero,
  type DxfExploreData,
  DxfLeftRail,
  DxfPreviewStage,
  PANEL_SURFACE_CLASS,
  dxfFilesQueryOptions,
  resolveCadFormat,
  useDxfCategories,
  useDxfFileExplore,
  useDxfFileVersions,
  useDxfFiles,
} from "@/features/dxf";
import { PriceListsPage } from "@/features/price-lists";
import { CadDataPanel, useOpenResult } from "@/features/structured-data";
import { validateWorkspaceSearch } from "@/features/workspace-router";
import { SubTabBar, useActiveSubTab } from "@/features/workspace/components/SubTabBar";
import type { SubTabDef } from "@/features/workspace/config";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import type { DxfFile } from "@ando/core";
import {
  HighlightNote,
  MetaItem,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceLayout,
  WorkspaceSearchBar,
  cn,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

export const Route = createFileRoute("/_authed/workspace/technical")({
  validateSearch: validateWorkspaceSearch,
  // Warm the default DXF file list (limit 200, default category) on
  // hover/focus so the hub paints from cache.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(dxfFilesQueryOptions({ params: { limit: 200 } }));
  },
  component: TechnicalHub,
});

/**
 * Technical hub — CAD library + viewer + actions.
 *
 * June 2026 SOTA redesign (UI/UX audit verdict "Preview-First"):
 *
 *   - The mechanical drawing IS the page. Canvas hero ≥520px tall,
 *     occupies the entire right column. Floating action bar overlays the
 *     drawing (BOM export, viewer mode, maximize, kebab) so the drawing
 *     is never crowded by chrome.
 *   - Left rail (280px) merges categories + file list into one compact
 *     navigation surface so the canvas reclaims the third column that
 *     the previous layout wasted on metadata.
 *   - Empty state replaced with ``DxfEmptyHero`` — engineering-context
 *     illustration + 4-step workflow + dual CTA. Communicates the value
 *     prop (DXF → blocks → BOM) instead of the bare "ancora nessun
 *     disegno" of the previous revision.
 *   - URL-driven selection (``?drawing=<content_item_id>``) so chat
 *     deep-links land on the right drawing and browser back/forward
 *     navigates between selected files.
 *   - Revisions live in a collapsible drawer below the canvas (no
 *     longer crammed into the right column).
 *
 * BE wiring is unchanged — still sources from ``/api/v1/dxf-library/*``.
 */

/** Recent-revision cutoff for the "Revisioni" tab. */
const REVISIONS_WINDOW_MS = 14 * 24 * 3600 * 1000;

// 2D bucket = mechanical DXF/DWG + the fashion 2D apparel pattern
// (``fashion-dxf``). 3D bucket = mechanical solids/meshes + the fashion
// garment mesh (``fashion-3d``) and the CLO/Marvelous project container
// (``fashion-project``). Fashion formats MUST live in one of these two
// sets — the file list only renders under ``drawings_2d`` / ``models_3d``,
// so a format missing from both would be invisible in the hub.
// ``pdf`` (Sep 1, 2026): PDF technical drawings pass the BE ingestion
// auto-detect and carry cartiglio/PMI data — they ARE 2D drawings. The BE
// list already guards against generic document PDFs (``cad_analysis`` gate
// in ``_dxf_file_filter``), so every pdf row reaching this bucket is a
// bona-fide tavola tecnica.
const TWO_D_FORMATS = new Set<string>(["dxf", "dwg", "fashion-dxf", "pdf"]);
const THREE_D_FORMATS = new Set<string>([
  "step",
  "iges",
  "stl",
  "fashion-3d",
  "fashion-project",
]);

function filterBySubTab(files: readonly DxfFile[], subTab: string): readonly DxfFile[] {
  if (subTab === "drawings_2d") {
    return files.filter((f) => TWO_D_FORMATS.has(resolveCadFormat(f)));
  }
  if (subTab === "models_3d") {
    return files.filter((f) => THREE_D_FORMATS.has(resolveCadFormat(f)));
  }
  if (subTab === "assemblies") {
    // Phase 1a STEP assembly tree → ``cad_analysis.semantics.assembly``
    // surfaces ``is_assembly: true`` rows. The list endpoint passes the
    // analyzer payload through (loose ``[k]: unknown`` on ``DxfFile``)
    // so we read it without a schema-version coupling.
    return files.filter((f) => {
      const ca = (f as Record<string, unknown>).cad_analysis as
        | { semantics?: { assembly?: ReadonlyArray<{ is_assembly?: boolean }> } }
        | undefined;
      const assembly = ca?.semantics?.assembly ?? [];
      return Array.isArray(assembly) && assembly.some((n) => n?.is_assembly);
    });
  }
  if (subTab === "revisions") {
    const cutoff = Date.now() - REVISIONS_WINDOW_MS;
    return files.filter((f) => {
      const t = Date.parse(f.updated_at ?? f.created_at ?? "");
      return Number.isFinite(t) && t >= cutoff;
    });
  }
  if (subTab === "boms") {
    return [...files].sort((a, b) => (b.file_size ?? 0) - (a.file_size ?? 0));
  }
  return files;
}

function TechnicalHub() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authed/workspace/technical" });
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const activeSubTab = useActiveSubTab("technical");

  // Selection is URL-driven so chat deep-links + browser back/forward
  // both work natively. We reuse the existing ``?focus=<id>`` field
  // from ``WorkspaceSearchSchema`` (the same pattern documents.tsx
  // uses for its focused-view) — no new ad-hoc URL grammar.
  const selectedId = (search as { focus?: string }).focus ?? null;
  const setSelectedId = useCallback(
    (id: string | null) => {
      void navigate({
        to: "/workspace/technical",
        search: (prev) => ({ ...prev, focus: id ?? undefined }),
        replace: false,
      });
      // Auto-open the details drawer on first selection so the user
      // immediately sees revisions/description — but only if they
      // weren't already inspecting an existing selection.
      if (id && !selectedId) setDetailsOpen(true);
    },
    [navigate, selectedId],
  );

  const { files, total, isLoading, isError, refetch } = useDxfFiles({
    params: {
      category: selectedCategory ?? undefined,
      limit: 200,
    },
  });
  const { categories } = useDxfCategories();

  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);

  const { msgId: openResultMsgId, clearOpenResult } = useOpenResult();
  const panelOpen = openResultMsgId !== null;

  const searched = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return files;
    return files.filter((f) => f.filename.toLowerCase().includes(term));
  }, [files, searchValue]);

  const inSubTab = useMemo(() => filterBySubTab(searched, activeSubTab), [searched, activeSubTab]);

  const inReview = files.filter((f) => f.status === "pending" || f.status === "processing").length;

  const selectedFile = useMemo(() => {
    if (!selectedId) return null;
    return files.find((f) => f.content_item_id === selectedId) ?? null;
  }, [files, selectedId]);

  // Fetch rich CAD data (layers, annotations count, presigned preview
  // URL, description) for the selected file. ``list_dxf_files`` returns
  // only the basic content_items columns — preview generation per-row
  // would N+1 the entire library, so we lazily fetch /explore here.
  const explore = useDxfFileExplore(selectedId);

  // Merge the rich BE response into the selected file so existing
  // children (DxfPreviewStage, drawer) keep their narrow contract on
  // ``DxfFile``. Preview URL + download URL come from explore; the
  // basic identity stays from the list row.
  const enrichedFile = useMemo(() => {
    if (!selectedFile) return null;
    const e = explore.data;
    if (!e) return selectedFile;
    // Compose a same-origin proxy URL for the raw DXF bytes — drives
    // ``CadViewerSwitch`` (the WebGL pan/zoom viewer). Same pattern as
    // V1's ``buildDxfProxyUrl`` and as the BE preview proxy in
    // ``dxf_library._preview_url``. We don't append ``?user_id=…``
    // because the BE proxy uses the ``ando_session`` cookie for auth;
    // the legacy user_id query param was only ever for log breadcrumbs.
    const downloadUrl =
      e.minio_object_key && e.minio_bucket
        ? `/api/v1/documents/minio/file/${encodeURIComponent(e.minio_bucket)}/${e.minio_object_key
            .split("/")
            .map(encodeURIComponent)
            .join("/")}/proxy`
        : (selectedFile.download_url ?? null);
    return {
      ...selectedFile,
      preview_url: e.preview_url ?? selectedFile.preview_url ?? null,
      download_url: downloadUrl,
      description: selectedFile.description ?? e.description ?? null,
    };
  }, [selectedFile, explore.data]);

  const chips = useMemo<readonly SubTabDef[]>(
    () => [
      // Jul 2026 IA cleanup — tab count from 6 → 3.
      //   * Assemblies dropped (near-empty for most corpora — a filter
      //     inside Modelli 3D can surface them later)
      //   * Revisioni dropped (was time-window filter, not a category —
      //     ``updated_at`` in the card conveys the same signal)
      //   * Distinte dropped (BOM is per-file action; listing all files
      //     sorted by size was a weak grouping, confusing the count
      //     against 3D Models)
      // Result: a tight nav focused on the customer's actual mental
      // buckets — 2D drawings, 3D models, DFM rule library.
      {
        id: "drawings_2d",
        label: i18n._(msg`Disegni 2D`),
        badge: filterBySubTab(files, "drawings_2d").length,
      },
      {
        id: "models_3d",
        label: i18n._(msg`Modelli 3D`),
        badge: filterBySubTab(files, "models_3d").length,
      },
      // Sprint 1 (Jul 2026) — Rule library sits inside Technical so
      // designers see CAD + rules in the SAME hub. No badge count.
      { id: "dfm_rules", label: i18n._(msg`Regole progettazione`) },
      // Sprint 2 (Jul 10, 2026, issue #903) — "Fonti prezzi" is the
      // pricing sibling of DFM rules. Multi-source by design (only
      // upload ships today; Drive/ERP/DB register into the same rail
      // as they land — see ``src/services/price_lookup``).
      { id: "price_lists", label: i18n._(msg`Fonti prezzi`) },
    ],
    [files, i18n],
  );

  const handleSelectFile = useCallback(
    (f: DxfFile) => {
      setSelectedId(selectedId === f.content_item_id ? null : f.content_item_id);
    },
    [selectedId, setSelectedId],
  );

  return (
    <SurfaceLayout
      header={
        // Jul 2026 — single-row command bar (replaces stacked header + hero +
        // search + filter rows). The nav tab names the area; ``count`` carries
        // the summary; the view chips live behind the shared Filtri popover.
        <SurfaceCommandBar
          count={
            isLoading ? (
              <span className="text-ink-3">
                <Trans>Caricamento…</Trans>
              </span>
            ) : (
              <>
                <strong>
                  <span className="tabular-nums">{inSubTab.length}</span>
                  {selectedCategory || searchValue.trim() ? (
                    <Trans>
                      {" "}
                      di <span className="tabular-nums">{total}</span> disegni
                    </Trans>
                  ) : (
                    <Trans> disegni</Trans>
                  )}
                </strong>
                {inReview > 0 ? (
                  <Trans>
                    {" "}
                    · <span className="tabular-nums">{inReview}</span> in elaborazione
                  </Trans>
                ) : null}
              </>
            )
          }
          search={
            <WorkspaceSearchBar
              placeholder={i18n._(msg`Cerca disegno…`)}
              ariaLabel={i18n._(msg`Cerca disegni CAD`)}
              initialQuery={searchValue}
              onSearch={setSearchValue}
            />
          }
          views={<SubTabBar tabId="technical" chips={chips} className="!px-0 !py-0" />}
          actions={
            <QuickAddMenu
              area="technical"
              label={i18n._(msg`Nuovo`)}
              onAddCad={() =>
                navigate({
                  to: "/integrations/$capability",
                  params: { capability: "cad-library" },
                })
              }
              onOpenDfmRules={() =>
                navigate({
                  to: "/workspace/technical",
                  search: { sub: "dfm_rules" },
                })
              }
              onConfigureConnectors={() => navigate({ to: "/integrations" })}
            />
          }
        />
      }
    >
      {panelOpen ? (
        <CadDataPanel messageId={openResultMsgId} onClose={clearOpenResult} className="mb-4" />
      ) : null}
      <BriefingSlot
        domain="documents"
        className={cn("mb-5", panelOpen && "opacity-95")}
        storageKey="ando.briefing.technical"
        onLoaded={setBriefingHasContent}
      />

      {activeSubTab === "dfm_rules" ? (
        // Sprint 1 (Jul 2026) — DFM rule library rendered inline as a
        // sub-tab of Technical (not a separate route). The FE section
        // uses its own left sidebar (list of rule-sets) so we skip the
        // 2-column CAD grid entirely and let ``DfmRulesPage`` own the
        // full body real estate.
        <DfmRulesPage />
      ) : activeSubTab === "price_lists" ? (
        // Sprint 2 (Jul 10, 2026, issue #903) — Same layout contract as
        // ``dfm_rules``: the section owns its own left sidebar (list
        // summaries) so we bypass the CAD grid.
        <PriceListsPage />
      ) : isError ? (
        <SurfaceEmptyState
          icon={<Compass size={18} strokeWidth={1.75} />}
          title={i18n._(msg`Impossibile caricare i disegni`)}
          description={i18n._(msg`Verifica la connessione e riprova fra qualche secondo.`)}
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="hover:-translate-y-px rounded-md bg-ink-2 px-3 py-1.5 text-[12px] text-white"
            >
              <Trans>Riprova</Trans>
            </button>
          }
        />
      ) : (
        // ── PREVIEW-FIRST layout (June 2026) ────────────────────────────
        // 2-column grid: compact rail (280px) + hero canvas (flex-1).
        // ``min-h-[640px]`` ensures the canvas has real estate even on
        // shorter viewports (rail scrolls internally if list overflows).
        <div
          className={cn(
            "grid min-h-[640px] gap-4 lg:grid-cols-[280px_minmax(0,1fr)]",
            panelOpen && "opacity-95",
          )}
        >
          <DxfLeftRail
            files={inSubTab}
            categories={categories}
            selectedFileId={selectedId}
            selectedCategory={selectedCategory}
            totalCount={total}
            searchValue={searchValue}
            isLoading={isLoading}
            onSelectFile={handleSelectFile}
            onSelectCategory={setSelectedCategory}
            className="lg:max-h-[80vh]"
          />

          <div className="flex min-w-0 flex-col gap-3">
            <DxfPreviewStage
              file={enrichedFile}
              onDeleted={() => setSelectedId(null)}
              emptyState={
                <DxfEmptyHero
                  totalCount={total}
                  isFiltered={!!searchValue.trim() || !!selectedCategory}
                  onUpload={() => {
                    // Phase 2: open the upload sheet. For now we just
                    // funnel the user to the integrations hub where the
                    // CAD library connector lives.
                    void navigate({
                      to: "/integrations/$capability",
                      params: { capability: "cad-library" },
                    });
                  }}
                  onConnectDrive={() =>
                    void navigate({
                      to: "/integrations/$capability",
                      params: { capability: "drive" },
                    })
                  }
                  onOpenDfmRules={() =>
                    void navigate({
                      to: "/workspace/technical",
                      search: { sub: "dfm_rules" },
                    })
                  }
                />
              }
            />

            {/* DFM verdict — compact summary of the latest DFM analysis
                for the selected CAD, rendered inline BELOW the preview.
                Silent-null when: no file selected, no analysis yet (404),
                or the query is errored — keeps the stage clean when the
                tenant hasn't enabled DFM. */}
            {enrichedFile?.content_item_id ? (
              <DfmVerdictCard contentItemId={enrichedFile.content_item_id} />
            ) : null}

            {/* Collapsible details drawer — revisions + description +
                layers + annotations. Pushed BELOW the canvas (not
                crammed into the right column) so the drawing always
                wins the visual hierarchy. */}
            {selectedFile ? (
              <DrawingDetailsDrawer
                file={enrichedFile ?? selectedFile}
                explore={explore.data ?? null}
                exploreLoading={explore.isLoading}
                open={detailsOpen}
                onToggle={() => setDetailsOpen((v) => !v)}
              />
            ) : null}
          </div>
        </div>
      )}
    </SurfaceLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DrawingDetailsDrawer — collapsible drawer with revisions + description
// ─────────────────────────────────────────────────────────────────────

function DrawingDetailsDrawer({
  file,
  explore,
  exploreLoading,
  open,
  onToggle,
}: {
  file: DxfFile;
  explore: DxfExploreData | null;
  exploreLoading: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const { i18n } = useLingui();
  const layersCount = explore?.layers?.length ?? 0;
  const textsCount = explore?.texts_count ?? 0;
  const blocksCount = explore?.block_types?.length ?? 0;

  return (
    <section
      aria-label={i18n._(msg`Dettagli disegno`)}
      className={cn("overflow-hidden", PANEL_SURFACE_CLASS)}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="drawing-details-content"
        className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-card/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream motion-reduce:transition-none"
      >
        <SquareStack size={14} strokeWidth={1.75} aria-hidden className="text-ink-3" />
        <span className="flex-1 font-medium text-[12px] text-ink-2">
          <Trans>Dettagli, layer, revisioni</Trans>
        </span>
        {/* Inline summary meta so the user sees the richness even when
            the drawer is collapsed — same affordance V1 had as right-side
            "47 Layer / 50 Annotazioni". Pencil pass (Jul 2026): plain
            inline muted-ink counts, no filled/traffic-light chips. */}
        {!exploreLoading && (layersCount > 0 || textsCount > 0 || blocksCount > 0) ? (
          <span className="hidden items-center gap-x-3 sm:inline-flex">
            {layersCount > 0 ? (
              <MetaItem className="text-[10px] tabular-nums">
                <Trans>{layersCount} layer</Trans>
              </MetaItem>
            ) : null}
            {textsCount > 0 ? (
              <MetaItem className="text-[10px] tabular-nums">
                <Trans>{textsCount} annotazioni</Trans>
              </MetaItem>
            ) : null}
            {blocksCount > 0 ? (
              <MetaItem className="text-[10px] tabular-nums">
                <Trans>{blocksCount} blocchi</Trans>
              </MetaItem>
            ) : null}
          </span>
        ) : null}
        <ChevronDown
          size={14}
          strokeWidth={1.75}
          aria-hidden
          className={cn(
            "shrink-0 text-ink-3 transition-transform duration-200 motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div
          id="drawing-details-content"
          className="grid gap-4 border-hairline border-t px-4 py-4 md:grid-cols-[1fr_320px]"
        >
          <div className="space-y-4">
            <DescriptionBlock file={file} explore={explore} />
            {explore?.layers && explore.layers.length > 0 ? (
              <LayersBlock layers={explore.layers} />
            ) : null}
          </div>
          <RevisionsBlock fileId={file.content_item_id} />
        </div>
      ) : null}
    </section>
  );
}

function LayersBlock({
  layers,
}: {
  layers: ReadonlyArray<{ name: string; entity_count: number }>;
}) {
  const { i18n } = useLingui();
  return (
    <section aria-label={i18n._(msg`Layer del disegno`)} className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
          <SquareStack size={11} strokeWidth={1.75} aria-hidden />
          <Trans>Layer</Trans>
        </div>
        <span className="font-medium text-[10px] text-ink-3 tabular-nums">{layers.length}</span>
      </div>
      <ol className="grid gap-1 sm:grid-cols-2">
        {layers.map((l) => (
          <li
            key={l.name}
            className="flex items-center gap-2 rounded-lg border border-hairline px-2.5 py-1.5 text-[11px]"
          >
            <span className="min-w-0 flex-1 truncate font-mono text-ink-1">{l.name}</span>
            <span className="shrink-0 text-[10px] text-ink-3 tabular-nums">{l.entity_count}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function DescriptionBlock({
  file,
  explore,
}: {
  file: DxfFile;
  explore: DxfExploreData | null;
}) {
  // Fallback chain: user-edited description → BE vision analysis description.
  const text = file.description ?? explore?.description ?? null;
  const drawingType = explore?.drawing_type;
  const keyFeatures = explore?.key_features ?? [];

  if (!text && !drawingType && keyFeatures.length === 0) {
    return (
      <div className="rounded-lg border border-hairline border-dashed bg-card/30 p-4 text-[12px] text-ink-3">
        <Trans>
          Nessuna descrizione registrata · Ando genera automaticamente la descrizione e i blocchi
          rilevati quando processa il file.
        </Trans>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
        <Layers3 size={11} strokeWidth={1.75} aria-hidden />
        <Trans>Descrizione</Trans>
        {drawingType ? (
          <span className="rounded-md bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[10px] text-ando-orange">
            {drawingType}
          </span>
        ) : null}
      </div>
      {text ? (
        <HighlightNote tone="neutral" className="text-[12px] text-ink-2 leading-relaxed">
          {text}
        </HighlightNote>
      ) : null}
      {keyFeatures.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {keyFeatures.slice(0, 8).map((feat) => (
            <li
              key={feat}
              className="rounded-md border border-hairline px-2 py-0.5 text-[11px] text-ink-2"
            >
              {feat}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RevisionsBlock({ fileId }: { fileId: string }) {
  const { i18n } = useLingui();
  const { versions, isLoading, isError } = useDxfFileVersions(fileId);

  return (
    <section aria-label={i18n._(msg`Cronologia revisioni`)} className="space-y-1.5">
      <div className="flex items-center gap-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
        <Clock size={11} strokeWidth={1.75} aria-hidden />
        <Trans>Cronologia revisioni</Trans>
      </div>

      {isLoading ? (
        <div className="rounded-lg bg-card/40 px-3 py-2.5 text-[11px] text-ink-3">
          <Trans>Caricamento…</Trans>
        </div>
      ) : isError ? (
        <div className="rounded-lg bg-card/40 px-3 py-2.5 text-[11px] text-ink-3">
          <Trans>Impossibile caricare la cronologia.</Trans>
        </div>
      ) : versions.length === 0 ? (
        <div className="rounded-lg border border-hairline border-dashed bg-card/30 px-3 py-2.5 text-[11px] text-ink-3">
          <Trans>Nessuna revisione · il disegno è nello stato originale.</Trans>
        </div>
      ) : (
        <ol className="flex flex-col gap-1">
          {versions.map((v) => {
            const ts = formatRelativeTime(v.created_at);
            const label =
              v.modification_summary ??
              v.modification_type ??
              i18n._(msg`Revisione #${v.version_number}`);
            return (
              <li
                key={v.id}
                className="flex items-center gap-2 rounded-lg border border-hairline px-2.5 py-1.5 text-[11px]"
              >
                <span className="shrink-0 rounded-md bg-ando-orange-soft px-1.5 py-0.5 font-medium text-[10px] text-ando-orange tabular-nums">
                  v{v.version_number}
                </span>
                <span className="min-w-0 flex-1 truncate text-ink-1">{label}</span>
                {ts ? (
                  <span className="shrink-0 text-[10px] text-ink-3 tabular-nums">{ts}</span>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
